import { useState } from 'react';
import { ChevronRight, Folder } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { useBoardChildren, useLibraries, useLibraryBoards } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { cx } from '@/lib/format';

/** Tree picker for move/copy targets and board moves. */
export function BoardPickerDialog({ title, confirmLabel, exclude = [], onPick, onClose, allowLibraryRoot = false }: { title: string; confirmLabel: string; exclude?: string[]; onPick: (target: { boardId: string | null; libraryId: string }) => void; onClose: () => void; allowLibraryRoot?: boolean }) {
  const wsId = useSessionStore((s) => s.workspace?.id);
  const libs = useLibraries(wsId);
  const [sel, setSel] = useState<{ boardId: string | null; libraryId: string } | null>(null);
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!sel} onClick={() => sel && onPick(sel)}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="max-h-80 space-y-2 overflow-y-auto">
        {libs.data?.map((lib) => (
          <div key={lib.id}>
            <button
              type="button"
              disabled={!allowLibraryRoot}
              onClick={() => setSel({ boardId: null, libraryId: lib.id })}
              className={cx('w-full rounded px-2 py-1 text-left text-xs font-semibold tracking-wide text-fg-subtle uppercase', allowLibraryRoot && 'hover:bg-bg-sunken', sel?.libraryId === lib.id && sel.boardId === null && 'bg-accent-soft text-accent')}
            >
              {lib.name}
            </button>
            <PickerLibrary libraryId={lib.id} exclude={exclude} sel={sel?.boardId ?? null} onSel={(b) => setSel({ boardId: b.id, libraryId: b.library_id })} />
          </div>
        ))}
      </div>
    </Dialog>
  );
}

function PickerLibrary({ libraryId, exclude, sel, onSel }: { libraryId: string; exclude: string[]; sel: string | null; onSel: (b: Board) => void }) {
  const roots = useLibraryBoards(libraryId);
  return <ul>{roots.data?.map((b) => <PickerNode key={b.id} board={b} depth={0} exclude={exclude} sel={sel} onSel={onSel} />)}</ul>;
}

function PickerNode({ board, depth, exclude, sel, onSel }: { board: Board; depth: number; exclude: string[]; sel: string | null; onSel: (b: Board) => void }) {
  const [open, setOpen] = useState(false);
  const children = useBoardChildren(open ? board.id : undefined);
  const disabled = exclude.includes(board.id);
  return (
    <li>
      <div className={cx('flex items-center gap-1 rounded-md', sel === board.id && 'bg-accent-soft')} style={{ paddingLeft: depth * 14 }}>
        <button type="button" aria-label={open ? 'Collapse' : 'Expand'} className={cx('h-6 w-6 text-fg-subtle', board.child_board_count === 0 && 'invisible')} onClick={() => setOpen((o) => !o)}>
          <ChevronRight size={13} className={cx('mx-auto transition-transform', open && 'rotate-90')} />
        </button>
        <button type="button" disabled={disabled} onClick={() => onSel(board)} className={cx('flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-1 text-left text-sm hover:bg-bg-sunken disabled:opacity-40', sel === board.id && 'text-accent')}>
          <Folder size={14} style={{ color: board.color ?? undefined }} />
          <span className="truncate">{board.name}</span>
        </button>
      </div>
      {open && <ul>{children.data?.filter((c) => !exclude.includes(c.id)).map((c) => <PickerNode key={c.id} board={c} depth={depth + 1} exclude={exclude} sel={sel} onSel={onSel} />)}</ul>}
    </li>
  );
}
